import { useState, useCallback, useRef, useEffect } from 'react';
import type { Chart as ApiChart } from '../../api';
import { chartsService } from '../../api/chartsService';
import type { ChartEditorState } from '../../components/edit/visualConfig';
import { isFieldConfigComplete, DEFAULT_SORT_LIMIT } from '../../components/edit/visualConfig';

interface UseChartPreviewOptions {
  chart: ApiChart | null;
  editorState: ChartEditorState;
  dashboardId: string | undefined;
  /** First chart's dataset view ID (fallback when chart has none) */
  fallbackDatasetViewId: string | null | undefined;
  /** Called when an AI/SQL preview arrives so editor state can be synced */
  syncEditorStateFromChart: (c: ApiChart) => void;
  /** Release the field-update freeze in the editor state hook */
  setIsPendingFieldUpdate: (v: boolean) => void;
  /** Refs for debounce change detection (owned by useVisualConfigSync) */
  fieldsJsonRef: React.MutableRefObject<string>;
  sortLimitJsonRef: React.MutableRefObject<string>;
}

/**
 * Manages the chart preview lifecycle:
 * - Preview key tracking (AI / SQL / field-change sources)
 * - Debounced field-change → preview updates
 * - SQL editor execution
 * - Preview chart fetching from API
 * - Refresh trigger for data reload
 */
export function useChartPreview({
  chart,
  editorState,
  dashboardId,
  fallbackDatasetViewId,
  syncEditorStateFromChart,
  setIsPendingFieldUpdate,
  fieldsJsonRef,
  sortLimitJsonRef,
}: UseChartPreviewOptions) {
  // ── State ────────────────────────────────────────────────────────────────

  const [previewChart, setPreviewChart] = useState<ApiChart | null>(null);
  const [previewChartId, setPreviewChartId] = useState<string | null>(null);
  const previewChartIdRef = useRef<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Track whether the pending preview came from AI/SQL (vs. field changes)
  const aiPreviewPendingRef = useRef(false);

  // SQL execution state
  const [isSqlExecuting, setIsSqlExecuting] = useState(false);
  const [sqlError, setSqlError] = useState<string | null>(null);

  // ── AI / external preview update ──────────────────────────────────────

  const handlePreviewUpdate = useCallback((previewKey: string, _reason: string) => {
    aiPreviewPendingRef.current = true;
    setPreviewChartId(previewKey);
    previewChartIdRef.current = previewKey;
    // AI edits can update an existing Redis preview key in place. Always bump
    // the trigger so metadata and chart data reload even when the key is unchanged.
    setRefreshTrigger((v) => v + 1);
  }, []);

  // ── SQL editor execution ──────────────────────────────────────────────

  const handleSqlExecute = useCallback(async (sql: string) => {
    const dvId = chart?.datasetViewId ?? fallbackDatasetViewId;
    if (!dvId) {
      setSqlError('No dataset view available. Please select a dataset first.');
      return;
    }

    setIsSqlExecuting(true);
    setSqlError(null);

    try {
      const result = await chartsService.updateChartPreview({
        chartId: chart?.id ?? 'new',
        datasetViewId: dvId,
        dashboardId,
        previewKey: previewChartIdRef.current ?? undefined,
        sql,
        chartType: editorState.chartType,
        fields: editorState.fields as Record<string, unknown>,
      });

      if (result.preview_key) {
        aiPreviewPendingRef.current = true;
        previewChartIdRef.current = result.preview_key;
        setPreviewChartId(result.preview_key);
        setRefreshTrigger((v) => v + 1);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'SQL execution failed';
      setSqlError(message);
      console.error('SQL execution failed:', err);
    } finally {
      setIsSqlExecuting(false);
    }
  }, [chart?.id, chart?.datasetViewId, fallbackDatasetViewId, editorState.chartType, editorState.fields]);

  const handleSqlErrorClear = useCallback(() => {
    setSqlError(null);
  }, []);

  // ── Field change → debounced preview update ───────────────────────────

  const previewUpdateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const newFieldsJson = JSON.stringify(editorState.fields ?? null);
    const newSortLimitJson = JSON.stringify({
      sort: editorState.chartFieldsConfig.sort,
      limit: editorState.chartFieldsConfig.limit,
      limitOthers: editorState.chartFieldsConfig.limitOthers,
      sortField: editorState.chartFieldsConfig.sortField,
      sortDirection: editorState.chartFieldsConfig.sortDirection,
      breakdownSort: editorState.chartStyle.breakdownSort,
      scatterMode: editorState.chartFieldsConfig.scatterMode,
    });

    const fieldsChanged = newFieldsJson !== fieldsJsonRef.current;
    const sortLimitChanged = newSortLimitJson !== sortLimitJsonRef.current;

    if (!fieldsChanged && !sortLimitChanged) return;

    fieldsJsonRef.current = newFieldsJson;
    sortLimitJsonRef.current = newSortLimitJson;

    // Skip if minimum required fields are not set — release freeze
    if (!isFieldConfigComplete(editorState.chartType, editorState.fields)) {
      setIsPendingFieldUpdate(false);
      return;
    }

    const dvId = chart?.datasetViewId ?? fallbackDatasetViewId;
    if (!dashboardId || !dvId) {
      setIsPendingFieldUpdate(false);
      return;
    }

    if (previewUpdateTimeoutRef.current) {
      clearTimeout(previewUpdateTimeoutRef.current);
    }

    previewUpdateTimeoutRef.current = setTimeout(async () => {
      try {
        const sortLimitDefaults =
          DEFAULT_SORT_LIMIT[editorState.chartType] ?? DEFAULT_SORT_LIMIT.bar;
        const chartFieldsConfig: Record<string, unknown> = {
          sort: editorState.chartFieldsConfig.sort ?? sortLimitDefaults.sort,
          limit: editorState.chartFieldsConfig.limit !== undefined ? editorState.chartFieldsConfig.limit : sortLimitDefaults.limit,
          limitOthers: editorState.chartFieldsConfig.limitOthers ?? sortLimitDefaults.limitOthers,
          sortField: editorState.chartFieldsConfig.sortField ?? null,
          sortDirection: editorState.chartFieldsConfig.sortDirection ?? null,
          scatterMode: editorState.chartFieldsConfig.scatterMode ?? null,
        };
        const chartStyle: Record<string, unknown> = {
          breakdownSort: editorState.chartStyle.breakdownSort ?? null,
        };

        const result = await chartsService.updateChartPreview({
          chartId: chart?.id ?? 'new',
          datasetViewId: dvId,
          dashboardId,
          previewKey: previewChartIdRef.current ?? undefined,
          fields: editorState.fields as Record<string, unknown>,
          chartStyle,
          chartFieldsConfig,
          chartType: editorState.chartType,
        });
        if (result.preview_key) {
          previewChartIdRef.current = result.preview_key;
          setPreviewChartId(result.preview_key);
          setRefreshTrigger((v) => v + 1);
        }
      } catch (err) {
        console.error('Failed to update chart preview from field change:', err);
      }
    }, 500);

    return () => {
      if (previewUpdateTimeoutRef.current) {
        clearTimeout(previewUpdateTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    editorState.fields, editorState.chartType,
    editorState.chartFieldsConfig.sort, editorState.chartFieldsConfig.limit, editorState.chartFieldsConfig.limitOthers,
    editorState.chartFieldsConfig.sortField, editorState.chartFieldsConfig.sortDirection,
    editorState.chartFieldsConfig.scatterMode,
    editorState.chartStyle.breakdownSort,
    dashboardId, chart?.id, chart?.datasetViewId,
  ]);

  // ── Fetch preview chart metadata ──────────────────────────────────────

  useEffect(() => {
    const fetchPreviewChart = async () => {
      if (!previewChartId) {
        setPreviewChart(null);
        return;
      }
      try {
        const fetched = await chartsService.getChart(previewChartId);
        setPreviewChart(fetched);

        if (aiPreviewPendingRef.current) {
          aiPreviewPendingRef.current = false;
          syncEditorStateFromChart(fetched);
        }
      } catch (err) {
        console.error('Failed to load preview chart:', err);
        setPreviewChart(null);
      }
    };
    fetchPreviewChart();
  }, [previewChartId, dashboardId, refreshTrigger, syncEditorStateFromChart]);

  // ── Reset on chart ID change ──────────────────────────────────────────

  const resetPreviewState = useCallback(() => {
    setPreviewChart(null);
    setPreviewChartId(null);
    previewChartIdRef.current = null;
  }, []);

  return {
    previewChart,
    previewChartId,
    previewChartIdRef,
    refreshTrigger,
    setRefreshTrigger,
    handlePreviewUpdate,
    handleSqlExecute,
    handleSqlErrorClear,
    isSqlExecuting,
    sqlError,
    resetPreviewState,
  };
}